/* Copyright (c) Trimble Inc. All rights reserved. */

import React from 'react';
import { Box, Grid, Typography, TypographyProps, useMediaQuery, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { ANIMATION_CONSTANTS } from 'modus/core.constants';


export interface GenericPageTitleProps {
  title?: JSX.Element | string;
  caption?: JSX.Element | string;
  subTitle?: JSX.Element | string;
  description?: JSX.Element | string;
  controls?: JSX.Element | string;
  noControls?: boolean;
  noDescription?: boolean;
  noSubTitle?: boolean;
  noTitle?: boolean;
  noCaption?: boolean;
  titleProps?: TypographyProps;
  subTitleProps?: TypographyProps;
  noSkeleton?: boolean;
}

/**
 * @component GenericPageTitle
 * @description Every page in application have to maintain consistent theming across pages.
 * This component will provide theming for Title, Subtitle and Controls of the page.
 * @param title Mentions what this page is all about
 * @param subTitle If you have multiple pages for same title, ensure to add subtitle to differentiate pages.
 * @param description (Optional) to add details about the page in general.
 * @param controls additional configurations about the title / page / domain
 * @param noTitle optional boolean - added to avoid loader skeleton if title is not added
 * @param noSubTitle optional boolean - added to avoid loader sub title if title is not added
 * @param noDescription optional boolean - added to avoid loader skeleton if description is not added
 * @param noControls optional boolean - added to avoid loader skeleton if controls is not added
 * @returns
 */
export const GenericPageTitle2: React.FC<GenericPageTitleProps> = ({
  title,
  subTitle,
  description,
  controls,
  noControls = false,
  noDescription = false,
  noSubTitle = false,
  noTitle = false,
  subTitleProps,
  titleProps,
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme?.breakpoints?.down('md'));
  return (
    <Grid
      container
      style={{ marginBottom: '24px', flexDirection: matches ? 'column' : 'row' }}
    >
      <Grid item xl={8} md={8} sm={12} xs={12} style={{ flexGrow: 1 }}>
        <Grid container>
          {/* Page Title */}
          {!noTitle && (
            <Grid item xs={12} style={{ justifyContent: 'center' }}>
              {title && (
                <Typography variant="h1" className="banner2" style={{fontSize: "24px", fontWeight: 500}} {...titleProps}>
                  {title}
                </Typography>
              )}
              {!title && (
                <Skeleton animation="wave" height={'33px'} width={'100%'} />
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      {/* Page  Configuration & controls */}
      {!noControls && (
        <Grid
          item
          xl={4}
          md={4}
          xs={12}
          sm={12}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          {controls && <Box alignSelf={'end'}>{controls}</Box>}
          {!controls && (
            <Box style={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <Skeleton
                variant="circle"
                animation={ANIMATION_CONSTANTS.SKELETON_TYPE}
                width={32}
                height={32}
              />
            </Box>
          )}
        </Grid>
      )}
      {/* Page SubTitle */}
      {!noSubTitle && (
        <Grid item xs={12} style={{ marginTop: noTitle ? 0 : '4px' }}>
          {subTitle && (
            <Typography variant="h2"  style={{fontSize: "14px"}}  {...subTitleProps}>
              {subTitle}
            </Typography>
          )}
          {!subTitle && (
            <Skeleton animation="wave" height={'27px'} width={'100%'} />
          )}
        </Grid>
      )}
      {/* Page  Description */}
      {!noDescription && (
        <Grid item xs={12} style={{ marginTop: '4px' }}>
          {description && (
            <Typography variant="body1">{description}</Typography>
          )}
          {!description && (
            <Skeleton
              animation={ANIMATION_CONSTANTS.SKELETON_TYPE}
              width={'100%'}
              height="18px"
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

export const GenericPageTitle: React.FC<GenericPageTitleProps> = React.memo(
  ({
    title,
    caption,
    subTitle,
    description,
    controls,
    noControls = false,
    noCaption = false,
    noSubTitle = false,
    noTitle = false,
    noDescription = false,
    noSkeleton = false,
    subTitleProps,
    titleProps,
  }) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme?.breakpoints?.down('md'));
    return (
      <Box style={{ marginBottom: '7px' }} className="title-container">
        <Box flexDirection={matches ? 'column' : 'row'}>
          <Box flexGrow={1} flexShrink={1}>
            {/* Page Title */}
            {!noTitle && (
              <>
                {title && (
                  <Typography variant="h4" style={{fontSize:"24px", fontWeight: "normal"}} {...titleProps}>
                    {title}
                  </Typography>
                )}
                {(!noSkeleton && !title) && (
                  <Skeleton animation="wave" height={'33px'} width={'30%'} />
                )}
              </>
            )}
          </Box>
          <Box
            flexGrow={1}
            flexShrink={0}
            alignContent="flex-end"
            justifyContent="flex-start"
            pt="10px"
          >
            {/* Page  Configuration & controls */}
            {!noControls && (
              <>
                {controls && <Box alignSelf={'end'}>{controls}</Box>}
                {(!noSkeleton && !controls) && (
                  <Box style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                    <Skeleton
                      variant="circle"
                      animation={ANIMATION_CONSTANTS.SKELETON_TYPE}
                      width={32}
                      height={32}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
        {/* Page Caption Link */}
        {!noCaption && <Box className="caption">{caption}</Box>}
        {/* Page SubTitle */}
        {!noSubTitle && (
          <Box style={{ marginTop: noTitle ? 0 : '4px' }} className="sub-title">
            {subTitle && (
              <Typography variant="h5" {...subTitleProps}>
                {subTitle}
              </Typography>
            )}
            {(!noSkeleton && !subTitle) && (
              <Skeleton animation="wave" height={'27px'} width={'50%'} />
            )}
          </Box>
        )}
        {!noDescription && (
          <Grid item xs={12} style={{ marginTop: '4px' }} className="description">
            {description && (
              <Typography variant="body1" style={{fontSize: "14px", fontWeight: 400}}>{description}</Typography>
            )}
            {(!noSkeleton && !description) && (
              <Skeleton
                animation={ANIMATION_CONSTANTS.SKELETON_TYPE}
                width={'50%'}
                height="18px"
              />
            )}
          </Grid>
        )}
      </Box>
    );
  }
);

export default GenericPageTitle;
